import { getIdentity } from '@sdc/auth';
import { KeyRound, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Splash } from '../components/Splash';
import { SynthwaveBg } from '../components/SynthwaveBg';
import { logoColor } from '../lib/logos';
import { authLink, describeAuthError } from '../lib/authLink';
import { consumeAuthTokens } from '../lib/consumeAuthTokens';
import { useSessionWait } from '../lib/useSessionWait';

const MIN_LENGTH = 10;

/**
 * Passwort setzen – Ziel von Invite- und Recovery-Links, und aus dem Dashboard
 * heraus erreichbar.
 *
 * Ohne diesen Schritt haette ein eingeladener User nie ein Passwort und kaeme
 * nur per Magic Link rein.
 */
export function SetPasswordPage() {
  const navigate = useNavigate();
  const { state, email } = useSessionWait();
  const [password, setPassword] = useState('');

  // Falls der Link direkt hier landet: Tokens einloesen (idempotent).
  useEffect(() => {
    void consumeAuthTokens();
  }, []);

  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (password.length < MIN_LENGTH) {
      setError(`Mindestens ${MIN_LENGTH} Zeichen.`);
      return;
    }
    if (password !== repeat) {
      setError('Die beiden Eingaben sind nicht gleich.');
      return;
    }

    setBusy(true);
    const { error: updateError } = await getIdentity().auth.updateUser({ password });
    setBusy(false);

    if (updateError) {
      setError(updateError.message);
      return;
    }
    setDone(true);
    window.setTimeout(() => navigate('/', { replace: true }), 1200);
  }

  if (state === 'waiting') {
    return (
      <>
        <SynthwaveBg />
        <Splash label="Link wird geprüft …" />
      </>
    );
  }

  // Kein Redirect-Pingpong zum Login: lieber sagen, was los ist.
  if (state === 'none') {
    return (
      <>
        <SynthwaveBg />
        <div className="auth">
        <div className="auth__card">
          <div className="auth__brand">
            <img src={logoColor} alt="" width={42} height={42} />
            <span>
              SDC <em>HUB</em>
            </span>
          </div>
          <h1>Kein gültiger Link</h1>
          <div className="alert alert--error" role="alert">
            {describeAuthError() ??
              'Zum Setzen des Passworts braucht es eine Session – der Link ist abgelaufen, wurde schon benutzt, oder du bist nicht angemeldet.'}
          </div>
          <p className="card__meta">
            Auf der Login-Seite „Passwort vergessen?" antippen, dann den Link aus der Mail
            innerhalb von 60 Minuten öffnen.
          </p>
          <Link to="/login" replace className="px-btn px-btn--block">
            Zum Login
          </Link>
          <p className="auth__hint">
            Technische Details: type={authLink.type ?? '–'}, tokens=
            {authLink.hasTokens ? 'ja' : 'nein'}, code={authLink.hasCode ? 'ja' : 'nein'}
          </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SynthwaveBg />
      <div className="auth">
      <div className="auth__card">
        <div className="auth__brand">
          <img src={logoColor} alt="" width={42} height={42} />
            <span>
              SDC <em>HUB</em>
            </span>
        </div>

        <h1>Passwort setzen</h1>
        <p className="card__meta">
          {email ? `Für ${email}. ` : ''}Danach kannst du dich überall mit E-Mail und Passwort
          anmelden – oder weiter den Magic Link nutzen.
        </p>

        {error && (
          <div className="alert alert--error" role="alert">
            {error}
          </div>
        )}
        {done && (
          <div className="alert alert--success" role="status">
            Passwort gespeichert. Weiter zum Dashboard …
          </div>
        )}

        <form onSubmit={submit}>
          <label className="field">
            <span className="field__label">Neues Passwort (mind. {MIN_LENGTH} Zeichen)</span>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              required
              minLength={MIN_LENGTH}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field__label">Wiederholen</span>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              required
              minLength={MIN_LENGTH}
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
            />
          </label>
          <button type="submit" className="px-btn px-btn--block" disabled={busy || done}>
            {busy ? (
              <Loader2 size={18} className="spin" aria-hidden="true" />
            ) : (
              <KeyRound size={18} aria-hidden="true" />
            )}
            Speichern
          </button>
        </form>
      </div>
    </div>
    </>
  );
}
